import { useMemo, useState } from "react";
import classNames from "classnames";
import { Button, Flex, HelpIndicator, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { useGetAllCasesForProfileQuery } from "~/graphql/supergraphHooks";
import { useIOReusableProfiles } from "~/pages/Compliance/flags";
import { RelatedCasesModal } from "./RelatedCasesModal";
import * as styles from "./styles_vanilla.css";
import { getRelatedCases } from "./utils";

type Props = {
  currentCaseId: number;
  investingProfileId?: number | null;
  caseIsClosed: boolean;
  className?: string;
};

export const RelatedCasesPill = ({
  currentCaseId,
  investingProfileId,
  caseIsClosed,
  className,
}: Props) => {
  const ioReusableProfilesEnabled = useIOReusableProfiles();
  const [open, setOpen] = useState(false);

  const skip = !ioReusableProfilesEnabled || !investingProfileId || caseIsClosed;

  const { data } = useGetAllCasesForProfileQuery({
    variables: { profileId: investingProfileId ?? 0 },
    skip,
  });

  const { relatedCases, count } = useMemo(() => {
    const cases = getRelatedCases(data, currentCaseId);
    return { relatedCases: cases, count: cases.length };
  }, [data, currentCaseId]);

  if (count === 0) return null;

  const label = `${count} related case${count === 1 ? "" : "s"}`;

  const tooltipContent = (
    <Flex className={styles.tooltipContent}>
      <Text variant="regular-md" className={styles.tooltipParagraph}>
        There are cases that share 1 or more profiles with this current case.
        <br />
        <br />
        Open cases: {count}
      </Text>
    </Flex>
  );

  return (
    <div className={classNames(styles.pillWrapper, className)}>
      <HelpIndicator
        id={`related-cases-pill-tooltip-${currentCaseId}`}
        placement="top"
        content={tooltipContent}
        fullWidth
      >
        <Button
          variant="custom"
          className={styles.pillButton}
          onClick={() => setOpen(true)}
          data-testid="related-cases-pill"
          icon="chevron-down"
          iconPosition="right"
        >
          <Icon
            type="folder-magnifying-glass"
            variant="solid"
            color={Colors.Warning600}
            className={styles.pillIcon}
          />
          <Text className={styles.pillLabel}>{label}</Text>
        </Button>
      </HelpIndicator>
      {open && (
        <RelatedCasesModal
          show={open}
          onHide={() => setOpen(false)}
          relatedCases={relatedCases}
        />
      )}
    </div>
  );
};
